// src/pages/OrdersPage.js
import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  collection,
  getDocs,
  deleteDoc,
  doc,
  setDoc,
  serverTimestamp,
  writeBatch, // <--- TAMBAHAN: Import writeBatch untuk hapus massal
} from 'firebase/firestore'
import { auth, db } from '../firebaseConfig'

// safe format tanggal
function formatDate(ts) {
  try {
    return ts && ts.toDate
      ? ts.toDate().toLocaleString('id-ID')
      : ts
      ? String(ts)
      : '-'
  } catch (e) {
    return '-'
  }
}

// serialisasi nilai untuk CSV/Excel
function valueToString(v) {
  if (v === null || v === undefined) return ''
  // Firestore Timestamp
  if (v && typeof v === 'object' && typeof v.toDate === 'function') {
    return formatDate(v)
  }
  if (typeof v === 'object') {
    try {
      return JSON.stringify(v)
    } catch (e) {
      return String(v)
    }
  }
  return String(v)
}

// Warna status
function statusColor(status) {
  switch (status) {
    case 'pending':
      return 'text-yellow-400'
    case 'accepted':
      return 'text-blue-400'
    case 'on_the_way':
      return 'text-indigo-400'
    case 'serving':
      return 'text-green-400'
    case 'completed':
      return 'text-purple-400'
    case 'cancelled':
      return 'text-red-400'
    default:
      return 'text-gray-300'
  }
}

export default function OrdersPage() {
  const navigate = useNavigate()
  const [orders, setOrders] = useState([])
  const [loading, setLoading] = useState(true)

  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')

  // --- BARU: State untuk navigasi Tab ---
  const [activeTab, setActiveTab] = useState('orders')

  // --- BARU: State penampung data ---
  const [issues, setIssues] = useState([])
  const [frauds, setFrauds] = useState([])
  const [mitraMap, setMitraMap] = useState({}) // <--- TAMBAHAN: Untuk mapping UID ke Nama Mitra

  // STATE BARU: Untuk menyimpan ID order yang dicentang
  const [selectedIds, setSelectedIds] = useState([])

  // UNDO delete
  const [deletedBackup, setDeletedBackup] = useState(null)
  const [undoTimer, setUndoTimer] = useState(null)

  // Pagination
  const [page, setPage] = useState(1)
  const rowsPerPage = 20

  // Fetch Orders, Issues, and Frauds (safe)
  useEffect(() => {
    let mounted = true
    const loadAllData = async () => {
      try {
        // 1. Fetch Orders
        const snapOrders = await getDocs(collection(db, 'orders'))
        const listOrders = snapOrders.docs
          .map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }))
          .sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0))

        // 2. Fetch Issues
        const snapIssues = await getDocs(collection(db, 'issue_tickets'))
        const listIssues = snapIssues.docs
          .map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }))
          .sort((a, b) => (b.timestamp?.seconds || 0) - (a.timestamp?.seconds || 0))

        // 3. Fetch Frauds
        const snapFrauds = await getDocs(collection(db, 'fraud_logs'))
        const listFrauds = snapFrauds.docs
          .map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }))
          .sort((a, b) => (b.timestamp?.seconds || 0) - (a.timestamp?.seconds || 0))

        // 4. Fetch Mitras (Terapis, Cleaner, Driver) untuk mapping nama di Export Excel/CSV
        const mapMitra = {}
        try {
          const [snapTherapists, snapCleaners, snapDrivers] = await Promise.all([
            getDocs(collection(db, 'therapists')),
            getDocs(collection(db, 'cleaners')),
            getDocs(collection(db, 'drivers'))
          ])
          
          snapTherapists.forEach(d => { mapMitra[d.id] = d.data().name || d.id })
          snapCleaners.forEach(d => { mapMitra[d.id] = d.data().name || d.id })
          snapDrivers.forEach(d => { mapMitra[d.id] = d.data().name || d.id })
        } catch (e) {
          console.warn("Gagal load data mitra untuk mapping:", e)
        }

        if (mounted) {
          setOrders(listOrders)
          setIssues(listIssues)
          setFrauds(listFrauds)
          setMitraMap(mapMitra) // Simpan kamus nama ke state
        }
      } catch (err) {
        console.error('Error loading data:', err)
      } finally {
        if (mounted) setLoading(false)
      }
    }
    loadAllData()
    return () => {
      mounted = false
    }
  }, [])

  // DELETE order (hapus order + chat + log) - guarded
  const deleteOrder = async (orderId) => {
    try {
      const c1 = window.confirm(`Hapus order ${orderId}?`)
      if (!c1) return
      // Hapus konfirmasi kedua agar lebih cepat, atau biarkan jika mau
      // const c2 = window.confirm('Chat juga akan dihapus. Lanjutkan?')
      // if (!c2) return

      const orderData = orders.find((o) => o.id === orderId)
      if (!orderData) return

      // backup
      setDeletedBackup(orderData)

      // delete operations
      await deleteDoc(doc(db, 'orders', orderId))
      // delete chat doc if exists (silently ignore errors)
      try {
        await deleteDoc(doc(db, 'chats', `order_${orderId}`))
      } catch (e) {
        console.warn('Failed deleting chat doc (may not exist):', e)
      }

      // log admin action (use addDoc alternative to be safe)
      try {
        await setDoc(doc(collection(db, 'admin_logs')), {
          action: 'delete_order',
          orderId,
          adminId: auth.currentUser?.uid || null,
          adminEmail: auth.currentUser?.email || null,
          timestamp: serverTimestamp(),
        })
      } catch (e) {
        console.warn('Failed writing admin log:', e)
      }

      // remove from UI
      setOrders((prev) => prev.filter((o) => o.id !== orderId))
      // Hapus dari selectedIds jika ada
      setSelectedIds((prev) => prev.filter((id) => id !== orderId))

      // start undo timer
      const timer = setTimeout(() => {
        setDeletedBackup(null)
        setUndoTimer(null)
      }, 30000)
      setUndoTimer(timer)

      alert('Order terhapus. Anda punya 30 detik untuk UNDO.')
    } catch (err) {
      console.error('Delete error:', err)
      alert('Gagal menghapus order. Cek console.')
    }
  }

  // FUNGSI BARU: Hapus Massal (Bulk Delete)
  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) return

    const confirmMsg = `Yakin ingin menghapus ${selectedIds.length} order yang dipilih secara PERMANEN? (Chat terkait juga akan dihapus)`
    if (!window.confirm(confirmMsg)) return

    try {
      setLoading(true)
      const batch = writeBatch(db)

      selectedIds.forEach((id) => {
        // Hapus Order
        const orderRef = doc(db, 'orders', id)
        batch.delete(orderRef)
        // Hapus Chat (asumsi ID chat = order_ID)
        const chatRef = doc(db, 'chats', `order_${id}`)
        batch.delete(chatRef)
      })

      await batch.commit()

      // Update UI
      setOrders((prev) => prev.filter((o) => !selectedIds.includes(o.id)))
      setSelectedIds([]) // Reset centang
      alert('Berhasil menghapus data terpilih.')
    } catch (err) {
      console.error('Bulk delete error:', err)
      alert('Gagal menghapus sebagian data.')
    } finally {
      setLoading(false)
    }
  }

  // UNDO restore
  const undoDelete = async () => {
    if (!deletedBackup) return
    try {
      await setDoc(doc(db, 'orders', deletedBackup.id), deletedBackup)
      setOrders((prev) => {
        // insert at top (sorted by createdAt likely)
        return [deletedBackup, ...prev]
      })
      clearTimeout(undoTimer)
      setUndoTimer(null)
      setDeletedBackup(null)
      alert('Order dipulihkan.')
    } catch (err) {
      console.error('Undo error:', err)
      alert('Gagal memulihkan order. Cek console.')
    }
  }

  // Filter + Search
  const filteredOrders = orders.filter((order) => {
    const q = search.trim().toLowerCase()
    const matchSearch =
      !q ||
      (order.userName && order.userName.toLowerCase().includes(q)) ||
      (order.orderId && order.orderId.toLowerCase().includes(q))
    const matchStatus = statusFilter === 'all' || order.status === statusFilter
    return matchSearch && matchStatus
  })

  // Pagination slicing
  const start = (page - 1) * rowsPerPage
  const end = start + rowsPerPage
  const displayed = filteredOrders.slice(start, end)

  // LOGIKA CHECKBOX
  // 1. Checkbox Header (Pilih Semua di halaman ini)
  const isAllSelected =
    displayed.length > 0 && displayed.every((o) => selectedIds.includes(o.id))

  const handleSelectAll = (e) => {
    const checked = e.target.checked
    const idsOnPage = displayed.map((o) => o.id)
    if (checked) {
      // Tambahkan yang belum ada
      const newIds = [...new Set([...selectedIds, ...idsOnPage])]
      setSelectedIds(newIds)
    } else {
      // Hapus ID yang ada di halaman ini dari state
      const newIds = selectedIds.filter((id) => !idsOnPage.includes(id))
      setSelectedIds(newIds)
    }
  }

  // 2. Checkbox Row (Pilih Satu)
  const handleSelectOne = (id) => {
    if (selectedIds.includes(id)) {
      setSelectedIds((prev) => prev.filter((item) => item !== id))
    } else {
      setSelectedIds((prev) => [...prev, id])
    }
  }

  // Export CSV (safe serialization)
  const exportCSV = () => {
    if (!orders || orders.length === 0) {
      alert('Tidak ada data untuk diexport')
      return
    }

    // Proses data: Ubah array UID di pingedMitras & ID Mitra Tunggal menjadi Nama
    const processedOrders = orders.map((o) => {
      let updatedOrder = { ...o }
      
      // 1. Terjemahkan Array UID di pingedMitras
      if (Array.isArray(updatedOrder.pingedMitras)) {
        updatedOrder.pingedMitras = updatedOrder.pingedMitras
          .map(uid => mitraMap[uid] || uid)
          .join(', ') 
      }
      
      // 2. Terjemahkan UID Mitra pemenang menjadi Nama (Terapis/Driver/Cleaner)
      if (updatedOrder.assignedTherapistId) {
        updatedOrder.assignedTherapistId = mitraMap[updatedOrder.assignedTherapistId] || updatedOrder.assignedTherapistId
      }
      if (updatedOrder.driverUid) {
        updatedOrder.driverUid = mitraMap[updatedOrder.driverUid] || updatedOrder.driverUid
      }
      if (updatedOrder.assignedCleanerId) {
        updatedOrder.assignedCleanerId = mitraMap[updatedOrder.assignedCleanerId] || updatedOrder.assignedCleanerId
      }

      return updatedOrder
    })

    // build a set of keys to include (stable order)
    const keysSet = new Set()
    processedOrders.forEach((o) => Object.keys(o).forEach((k) => keysSet.add(k)))
    const keys = Array.from(keysSet)

    const header = keys.join(',')
    const rows = processedOrders.map((o) =>
      keys
        .map((k) => {
          return `"${valueToString(o[k]).replace(/"/g, '""')}"` // CSV-safe quoted
        })
        .join(',')
    )

    const csv = [header, ...rows].join('\n')
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    link.href = URL.createObjectURL(blob)
    link.download = 'orders.csv'
    link.click()
  }

  // Export Excel (dynamic import)
  const exportExcel = async () => {
    if (!orders || orders.length === 0) {
      alert('Tidak ada data untuk diexport')
      return
    }
    try {
      const XLSX = await import('xlsx')
      const sheetData = orders.map((o) => {
        const out = {}
        Object.keys(o).forEach((k) => {
          if (k === 'pingedMitras' && Array.isArray(o[k])) {
            // Terjemahkan array UID pingedMitras
            out[k] = o[k].map(uid => mitraMap[uid] || uid).join(', ')
          } else if (k === 'assignedTherapistId' || k === 'driverUid' || k === 'assignedCleanerId') {
            // Terjemahkan UID Mitra Tunggal menjadi Nama
            out[k] = mitraMap[o[k]] || valueToString(o[k])
          } else {
            out[k] = valueToString(o[k])
          }
        })
        return out
      })
      const ws = XLSX.utils.json_to_sheet(sheetData)
      const wb = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(wb, ws, 'Orders')
      XLSX.writeFile(wb, 'orders.xlsx')
    } catch (err) {
      console.error('Export Excel error:', err)
      alert(
        'Gagal export Excel. Pastikan package xlsx sudah terinstall (npm i xlsx).'
      )
    }
  }

  if (loading) {
    return React.createElement(
      'div',
      { className: 'p-5 text-white' },
      'Loading...'
    )
  }

  // --- BARU: Komponen Navigasi Tab ---
  const renderTabs = () => {
    return React.createElement('div', { key: 'tabs', className: 'flex space-x-6 border-b border-gray-700 mb-6' }, [
      React.createElement('button', {
        key: 'tab-orders',
        className: `py-2 px-1 font-bold border-b-2 transition-colors ${activeTab === 'orders' ? 'border-green-500 text-green-500' : 'border-transparent text-gray-400 hover:text-white'}`,
        onClick: () => setActiveTab('orders')
      }, 'Semua Order'),
      React.createElement('button', {
        key: 'tab-issues',
        className: `py-2 px-1 font-bold border-b-2 transition-colors ${activeTab === 'issues' ? 'border-red-500 text-red-500' : 'border-transparent text-gray-400 hover:text-white'}`,
        onClick: () => setActiveTab('issues')
      }, 'Laporan Kendala'),
      React.createElement('button', {
        key: 'tab-fraud',
        className: `py-2 px-1 font-bold border-b-2 transition-colors ${activeTab === 'fraud' ? 'border-yellow-500 text-yellow-500' : 'border-transparent text-gray-400 hover:text-white'}`,
        onClick: () => setActiveTab('fraud')
      }, 'Log Anomali'),
    ]);
  };

  // --- UI KONTEN ORDER UTAMA LAMA ---
  const renderOrdersContent = () => {
    return React.createElement(React.Fragment, { key: 'orders-content' }, [
      // Filter bar (Beserta Tombol Hapus Massal)
      React.createElement('div', { key: 'filters', className: 'flex gap-3 flex-wrap justify-between mb-5' }, [
        // Kiri: Search & Filter
        React.createElement('div', { className: 'flex gap-3' }, [
          // Search
          React.createElement('input', {
              key: 'search',
              type: 'text',
              placeholder: 'Cari Order ID / Nama User...',
              className:
              'px-3 py-2 bg-gray-800 border border-gray-700 rounded text-white w-64',
              value: search,
              onChange: (e) => {
              setSearch(e.target.value)
              setPage(1)
              },
          }),
          // Status filter
          React.createElement(
              'select',
              {
              key: 'select',
              className:
                  'px-3 py-2 bg-gray-800 border border-gray-700 rounded text-white',
              value: statusFilter,
              onChange: (e) => {
                  setStatusFilter(e.target.value)
                  setPage(1)
              },
              },
              [
              'all',
              'pending',
              'accepted',
              'on_the_way',
              'serving',
              'completed',
              'cancelled',
              ].map((s) => React.createElement('option', { key: s, value: s }, s))
          ),
        ]),
        // Kanan: Tombol Action (Delete Selected, CSV, Excel)
        React.createElement('div', { className: 'flex gap-3' }, [
          // TOMBOL HAPUS MASSAL
          selectedIds.length > 0 && 
              React.createElement(
              'button',
              {
                  key: 'bulkDel',
                  className: 'px-3 py-2 bg-red-600 rounded text-white font-bold hover:bg-red-700 animate-pulse',
                  onClick: handleBulkDelete
              },
              `Hapus (${selectedIds.length}) Terpilih`
              ),
          // Export CSV
          React.createElement(
              'button',
              {
              key: 'csv',
              className:
                  'px-3 py-2 bg-yellow-600 rounded text-black font-bold hover:bg-yellow-700',
              onClick: exportCSV,
              },
              'Export CSV'
          ),
          // Export Excel
          React.createElement(
              'button',
              {
              key: 'xls',
              className: 'px-3 py-2 bg-green-600 rounded hover:bg-green-700',
              onClick: exportExcel,
              },
              'Export Excel'
          ),
        ])
      ]),

      // Table
      React.createElement(
        'div',
        {
          key: 'table',
          className: 'border border-gray-700 rounded-lg overflow-hidden',
        },
        React.createElement('table', { className: 'w-full text-left text-sm' }, [
          // HEAD
          React.createElement(
            'thead',
            { key: 'thead', className: 'bg-gray-800 text-gray-300' },
            React.createElement(
              'tr',
              null,
              [
                // CHECKBOX HEADER
                React.createElement('th', { key: 'chkAll', className: 'p-3 border-b border-gray-700 w-10 text-center' }, 
                   React.createElement('input', {
                      type: 'checkbox',
                      checked: isAllSelected,
                      onChange: handleSelectAll,
                      className: 'cursor-pointer w-4 h-4'
                   })
                ),
                'Order ID',
                'User',
                'Service',
                'Status',
                'Created',
                'Accepted',
                'Started',
                'Action',
              ].map((t, i) => {
                 if (typeof t === 'object') return t;
                 return React.createElement(
                  'th',
                  { key: i, className: 'p-3 border-b border-gray-700' },
                  t
                )
              })
            )
          ),
          // BODY
          React.createElement(
            'tbody',
            { key: 'tbody' },
            displayed.map((order) =>
              React.createElement(
                'tr',
                { key: order.id, className: `hover:bg-gray-800 ${selectedIds.includes(order.id) ? 'bg-gray-800 bg-opacity-80' : ''}` },
                [
                  // CHECKBOX ROW
                  React.createElement(
                      'td',
                      { key: 'chk', className: 'p-3 border-b border-gray-700 text-center' },
                      React.createElement('input', {
                          type: 'checkbox',
                          checked: selectedIds.includes(order.id),
                          onChange: () => handleSelectOne(order.id),
                          className: 'cursor-pointer w-4 h-4'
                      })
                  ),
                  React.createElement('td', { key: 'id', className: 'p-3 border-b border-gray-700' }, order.orderId || order.id),
                  React.createElement('td', { key: 'user', className: 'p-3 border-b border-gray-700' }, order.userName || '-'),
                  React.createElement('td', { key: 'svc', className: 'p-3 border-b border-gray-700' }, order.serviceName || '-'),
                  React.createElement(
                    'td',
                    {
                      key: 'st',
                      className: `p-3 border-b border-gray-700 font-semibold ${statusColor(order.status)}`,
                    },
                    order.status || '-'
                  ),
                  React.createElement('td', { key: 'c', className: 'p-3 border-b border-gray-700' }, formatDate(order.createdAt)),
                  React.createElement('td', { key: 'a', className: 'p-3 border-b border-gray-700' }, formatDate(order.acceptedAt)),
                  React.createElement('td', { key: 's', className: 'p-3 border-b border-gray-700' }, formatDate(order.startedAt)),
                  // ACTION
                  React.createElement(
                    'td',
                    { key: 'act', className: 'p-3 border-b border-gray-700 space-x-2' },
                    [
                      React.createElement(
                        'button',
                        {
                          key: 'view',
                          className: 'px-2 py-1 bg-blue-600 hover:bg-blue-700 rounded',
                          onClick: () => navigate(`/orders/${order.orderId || order.id}`),
                        },
                        'View'
                      ),
                      React.createElement(
                        'button',
                        {
                          key: 'chat',
                          className: 'px-2 py-1 bg-indigo-600 hover:bg-indigo-700 rounded',
                          onClick: () => navigate(`/orders/${order.orderId || order.id}/chat`),
                        },
                        'Chat'
                      ),
                      React.createElement(
                        'button',
                        {
                          key: 'del',
                          className: 'px-2 py-1 bg-red-600 hover:bg-red-700 rounded',
                          onClick: () => deleteOrder(order.id),
                        },
                        'Delete'
                      ),
                    ]
                  ),
                ]
              )
            )
          ),
        ])
      ),

      // Pagination
      React.createElement(
        'div',
        { key: 'pagination', className: 'flex justify-between mt-4' },
        [
          React.createElement(
            'button',
            {
              key: 'prev',
              className: 'px-3 py-1 bg-gray-700 rounded disabled:opacity-30 hover:bg-gray-600',
              disabled: page === 1,
              onClick: () => setPage(page - 1),
            },
            'Previous'
          ),
          React.createElement(
            'span',
            { key: 'info', className: 'text-gray-400' },
            `Page ${page} / ${Math.max(1, Math.ceil(filteredOrders.length / rowsPerPage))}`
          ),
          React.createElement(
            'button',
            {
              key: 'next',
              className: 'px-3 py-1 bg-gray-700 rounded disabled:opacity-30 hover:bg-gray-600',
              disabled: end >= filteredOrders.length,
              onClick: () => setPage(page + 1),
            },
            'Next'
          ),
        ]
      )
    ]);
  };

  // --- BARU: UI KONTEN LAPORAN KENDALA (ISSUES) ---
  const renderIssuesContent = () => {
    return React.createElement('div', { key: 'issues-table', className: 'border border-gray-700 rounded-lg overflow-hidden overflow-x-auto' }, 
      React.createElement('table', { className: 'w-full text-left text-sm' }, [
        React.createElement('thead', { key: 'thead', className: 'bg-gray-800 text-gray-300' }, 
          React.createElement('tr', null, ['Waktu', 'Order ID', 'Mitra ID', 'Kategori', 'Jarak (Meter)', 'Status', 'Foto Bukti', 'Aksi'].map((t, i) => 
            React.createElement('th', { key: i, className: 'p-3 border-b border-gray-700' }, t)
          ))
        ),
        React.createElement('tbody', { key: 'tbody' }, 
          issues.map((issue) => 
            React.createElement('tr', { key: issue.id, className: 'hover:bg-gray-800' }, [
              React.createElement('td', { key: 'time', className: 'p-3 border-b border-gray-700' }, formatDate(issue.timestamp)),
              React.createElement('td', { key: 'order', className: 'p-3 border-b border-gray-700' }, issue.orderId || '-'),
              React.createElement('td', { key: 'mitra', className: 'p-3 border-b border-gray-700 text-xs' }, issue.mitraId || '-'),
              React.createElement('td', { key: 'cat', className: 'p-3 border-b border-gray-700 font-bold text-red-400' }, issue.category || '-'),
              React.createElement('td', { key: 'dist', className: 'p-3 border-b border-gray-700' }, issue.distance_to_order_meters ? `${Math.round(issue.distance_to_order_meters)}m` : '-'),
              React.createElement('td', { key: 'st', className: 'p-3 border-b border-gray-700' }, issue.status || '-'),
              React.createElement('td', { key: 'photo', className: 'p-3 border-b border-gray-700' }, 
                issue.photoUrl 
                  ? React.createElement('a', { href: issue.photoUrl, target: '_blank', rel: 'noopener noreferrer' }, 
                      React.createElement('img', { src: issue.photoUrl, alt: 'Bukti', className: 'w-16 h-16 object-cover rounded border border-gray-600 hover:opacity-80' })
                    )
                  : 'Tidak ada foto'
              ),
              React.createElement('td', { key: 'act', className: 'p-3 border-b border-gray-700' }, 
                React.createElement('button', { 
                  className: 'px-2 py-1 bg-blue-600 hover:bg-blue-700 rounded text-xs',
                  onClick: () => navigate(`/orders/${issue.orderId}`)
                }, 'Cek Order')
              )
            ])
          )
        )
      ])
    );
  };

  // --- BARU: UI KONTEN LOG ANOMALI (FRAUDS) ---
  const renderFraudContent = () => {
    return React.createElement('div', { key: 'fraud-table', className: 'border border-gray-700 rounded-lg overflow-hidden overflow-x-auto' }, 
      React.createElement('table', { className: 'w-full text-left text-sm' }, [
        React.createElement('thead', { key: 'thead', className: 'bg-gray-800 text-gray-300' }, 
          React.createElement('tr', null, ['Waktu Kejadian', 'Order ID', 'Mitra ID', 'Jenis Pelanggaran', 'Terdeteksi Jarak'].map((t, i) => 
            React.createElement('th', { key: i, className: 'p-3 border-b border-gray-700' }, t)
          ))
        ),
        React.createElement('tbody', { key: 'tbody' }, 
          frauds.map((fraud) => 
            React.createElement('tr', { key: fraud.id, className: 'hover:bg-gray-800' }, [
              React.createElement('td', { key: 'time', className: 'p-3 border-b border-gray-700' }, formatDate(fraud.timestamp)),
              React.createElement('td', { key: 'order', className: 'p-3 border-b border-gray-700' }, fraud.orderId || '-'),
              React.createElement('td', { key: 'mitra', className: 'p-3 border-b border-gray-700 text-xs' }, fraud.mitraId || '-'),
              React.createElement('td', { key: 'type', className: 'p-3 border-b border-gray-700 font-bold text-yellow-500' }, fraud.anomaly_type || '-'),
              React.createElement('td', { key: 'dist', className: 'p-3 border-b border-gray-700' }, fraud.recorded_distance_meters ? `${Math.round(fraud.recorded_distance_meters)}m` : '-'),
            ])
          )
        )
      ])
    );
  };

  // UI UTAMA
  return React.createElement('div', { className: 'p-5 text-white space-y-5' }, [
    // UNDO bar
    deletedBackup &&
      React.createElement(
        'div',
        {
          key: 'undo',
          className:
            'p-3 bg-yellow-800 text-yellow-200 rounded-lg border border-yellow-600 flex justify-between',
        },
        [
          React.createElement(
            'span',
            { key: 'msg' },
            `Order ${deletedBackup.orderId} dihapus. Klik UNDO untuk mengembalikan.`
          ),
          React.createElement(
            'button',
            {
              key: 'btn',
              className:
                'px-3 py-1 bg-yellow-600 rounded text-black font-bold hover:bg-yellow-700',
              onClick: undoDelete,
            },
            'UNDO'
          ),
        ]
      ),

    // Header Title
    React.createElement(
      'h1',
      { key: 'title', className: 'text-2xl font-bold mb-2' },
      'Manajemen Order & Kendala Lapangan'
    ),

    // Render Tabs
    renderTabs(),

    // Render Konten Sesuai Tab Aktif
    activeTab === 'orders' 
      ? renderOrdersContent() 
      : activeTab === 'issues'
        ? renderIssuesContent()
        : renderFraudContent(),
  ]);
}